import React, { useState, useRef, useEffect, useCallback } from "react";
import PageForm, { TYPE_LABELS } from "./PageForm";
import CsvHelpModal from "../components/CsvHelpModal";
import {
  buildTemplate,
  downloadCsv,
  parseCsvToPages,
  validatePage,
} from "../utils/pageCsv";

const BASE = process.env.REACT_APP_BASE_URL;

/** Re-run validation for the whole list (keeps duplicate-slug checks in sync). */
function revalidateAll(list) {
  const seen = new Set();
  return list.map((e) => {
    const errors = validatePage(e.page, seen);
    if (e.page.slug) seen.add(e.page.slug);
    // Preserve a server error unless the row has been edited into validity.
    return { ...e, errors };
  });
}

export default function PageCsvImport({ type }) {
  const [entries, setEntries] = useState([]); // { id, page, errors, status, serverError }
  const [parseError, setParseError] = useState("");
  const [parseWarning, setParseWarning] = useState("");
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [creatingAll, setCreatingAll] = useState(false);

  const fileInputRef = useRef(null);
  const idCounter = useRef(0);

  // Keep a ref of the latest entries so async create loops read fresh state.
  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  /* ── template download ── */
  const handleDownload = () => {
    downloadCsv(`pages-${type}-template.csv`, buildTemplate(type));
    setHelpOpen(false);
  };

  /* ── file handling ── */
  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".csv")) {
        setParseError("Please select a .csv file.");
        return;
      }
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const result = parseCsvToPages(text, type);
        if (result.error) {
          setParseError(result.error);
          setParseWarning("");
          setEntries([]);
          return;
        }
        setParseError("");
        setParseWarning(result.warning || "");
        const list = result.entries.map((en) => ({
          id: ++idCounter.current,
          page: en.page,
          errors: en.errors,
          status: "pending",
          serverError: "",
        }));
        setEntries(list);
      };
      reader.onerror = () => setParseError("Could not read the file.");
      reader.readAsText(file);
    },
    [type]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  /* ── entry mutation helpers ── */
  const patchEntry = (id, patch) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const updateEntryPage = (id, updater) =>
    setEntries((prev) => {
      const next = prev.map((e) =>
        e.id === id
          ? { ...e, page: typeof updater === "function" ? updater(e.page) : updater }
          : e
      );
      return revalidateAll(next);
    });

  /* ── create a single page ── */
  const createOne = async (id) => {
    const entry = entriesRef.current.find((e) => e.id === id);
    if (!entry || entry.status === "created") return;

    if (entry.errors.length) {
      // Can't create an invalid page — send the user to the editor to fix it.
      setEditIndex(id);
      return false;
    }

    patchEntry(id, { status: "creating", serverError: "" });
    const slug = entry.page.slug;

    try {
      const checkRes = await fetch(
        `${BASE}/pages/check-slug?slug=${encodeURIComponent(slug)}`
      );
      if (!checkRes.ok) throw new Error("Could not verify slug with the server.");
      const taken = await checkRes.json();
      if (taken) {
        patchEntry(id, {
          status: "error",
          serverError: "This slug is already taken or reserved on the server. Change it and retry.",
        });
        return false;
      }

      const payload = { ...entry.page, slug, id: slug };
      const res = await fetch(`${BASE}/pages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        patchEntry(id, { status: "created", serverError: "" });
        return true;
      }
      patchEntry(id, {
        status: "error",
        serverError: "The server rejected this page (save failed). Review and retry.",
      });
      return false;
    } catch (err) {
      patchEntry(id, {
        status: "error",
        serverError: err.message || "Network error while saving. Retry.",
      });
      return false;
    }
  };

  /* ── create all pending valid pages, one by one ── */
  const createAll = async () => {
    setCreatingAll(true);
    // Snapshot ids up front; createOne reads fresh status from the ref.
    const ids = entriesRef.current.map((e) => e.id);
    for (const id of ids) {
      const entry = entriesRef.current.find((e) => e.id === id);
      if (!entry || entry.status === "created" || entry.errors.length) continue;
      // eslint-disable-next-line no-await-in-loop
      await createOne(id);
    }
    setCreatingAll(false);
  };

  const removeEntry = (id) =>
    setEntries((prev) => revalidateAll(prev.filter((e) => e.id !== id)));

  const resetImport = () => {
    setEntries([]);
    setFileName("");
    setParseError("");
    setParseWarning("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const pending = entries.filter((e) => e.status !== "created" && e.errors.length === 0);
  const invalidCount = entries.filter((e) => e.errors.length > 0).length;
  const createdCount = entries.filter((e) => e.status === "created").length;

  const editing = editIndex != null ? entries.find((e) => e.id === editIndex) : null;

  return (
    <div>
      {/* ── Intro + template download ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              Bulk import — {TYPE_LABELS[type] || type}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Download the template, fill one row per page, then upload it below. Pages are parsed
              and validated here in your browser — nothing is saved until you press Create.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="shrink-0 bg-[#4d8a82] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#3d6e68]"
          >
            Download demo CSV
          </button>
        </div>
      </div>

      {/* ── Upload area ── */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center mb-5 transition-colors ${
          dragging ? "border-[#4d8a82] bg-[#eef6f5]" : "border-gray-300 bg-gray-50"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {fileName ? (
          <div className="flex flex-col items-center gap-2">
            <span className="text-[#4d8a82] font-semibold break-all">{fileName}</span>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-gray-600 underline hover:text-[#4d8a82]"
              >
                Choose another file
              </button>
              <button
                type="button"
                onClick={resetImport}
                className="text-sm text-gray-500 underline hover:text-red-600"
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-600 font-medium">Drag & drop your filled CSV here</p>
            <p className="text-gray-400 text-sm my-1">— or —</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-5 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Browse files
            </button>
          </>
        )}
      </div>

      {parseError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm font-medium">
          {parseError}
        </div>
      )}
      {parseWarning && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 mb-4 text-sm">
          {parseWarning}
        </div>
      )}

      {/* ── Parsed pages list ── */}
      {entries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-gray-200">
            <div className="text-sm text-gray-600">
              <span className="font-bold text-gray-800">{entries.length}</span> pages parsed ·{" "}
              <span className="text-green-600 font-semibold">{createdCount} created</span> ·{" "}
              <span className="text-red-600 font-semibold">{invalidCount} need fixing</span>
            </div>
            <button
              type="button"
              onClick={createAll}
              disabled={creatingAll || pending.length === 0}
              className="bg-[#4d8a82] text-white px-5 py-2 rounded-lg font-semibold hover:bg-[#3d6e68] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingAll ? "Creating…" : `Create all (${pending.length})`}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-3 font-semibold">Row</th>
                  <th className="text-left p-3 font-semibold">Title</th>
                  <th className="text-left p-3 font-semibold">Slug</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.id} className="border-t border-gray-100 align-top">
                    <td className="p-3 text-gray-400">{i + 1}</td>
                    <td className="p-3">
                      <div className="font-medium text-gray-800">
                        {e.page.title || <span className="text-gray-400">(no title)</span>}
                      </div>
                      <div className="text-xs text-gray-400">{TYPE_LABELS[e.page.type] || e.page.type}</div>
                    </td>
                    <td className="p-3 text-gray-600 font-mono text-xs">
                      /stores/{e.page.slug || "—"}
                    </td>
                    <td className="p-3">
                      <StatusCell entry={e} />
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        {e.status === "created" ? (
                          <a
                            href={`/edit-page/${e.page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 rounded text-white bg-gray-500 hover:bg-gray-600 text-xs font-semibold"
                          >
                            View
                          </a>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditIndex(e.id)}
                              className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-semibold"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => createOne(e.id)}
                              disabled={e.status === "creating"}
                              className={`px-3 py-1.5 rounded text-white text-xs font-semibold disabled:opacity-50 ${
                                e.errors.length
                                  ? "bg-amber-500 hover:bg-amber-600"
                                  : "bg-[#4d8a82] hover:bg-[#3d6e68]"
                              }`}
                            >
                              {e.status === "creating"
                                ? "Creating…"
                                : e.errors.length
                                ? "Fix & create"
                                : "Create"}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeEntry(e.id)}
                              className="px-2 py-1.5 rounded text-gray-400 hover:text-red-600 text-xs"
                              title="Remove from list"
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Help / template modal ── */}
      <CsvHelpModal
        isOpen={helpOpen}
        type={type}
        onClose={() => setHelpOpen(false)}
        onDownload={handleDownload}
      />

      {/* ── Per-row edit modal (reuses the full PageForm) ── */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <div className="bg-gray-50 rounded-xl shadow-xl w-full max-w-4xl my-6">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-xl sticky top-0 z-10">
              <h3 className="text-lg font-bold text-gray-800">
                Edit page — {editing.page.slug || "(no slug)"}
              </h3>
              <button
                onClick={() => setEditIndex(null)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-5">
              {editing.serverError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm font-medium">
                  {editing.serverError}
                </div>
              )}
              {editing.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 mb-4 text-sm">
                  <p className="font-semibold mb-1">Fix these before creating:</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {editing.errors.map((err, ei) => (
                      <li key={ei}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <PageForm
                formData={editing.page}
                setFormData={(updater) => updateEntryPage(editing.id, updater)}
                slugError={/slug/i.test(editing.serverError || "") ? editing.serverError : ""}
                slugDisabled={false}
                onSubmit={async (ev) => {
                  ev.preventDefault();
                  const ok = await createOne(editing.id);
                  if (ok) setEditIndex(null);
                }}
                submitting={editing.status === "creating"}
                footer={
                  <div className="flex flex-col sm:flex-row gap-3 pb-4">
                    <button
                      type="submit"
                      disabled={editing.status === "creating"}
                      className="flex-1 bg-[#4d8a82] text-white font-bold py-3 rounded-xl hover:bg-[#3d6e68] disabled:opacity-60"
                    >
                      {editing.status === "creating" ? "Creating…" : "Create this page"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditIndex(null)}
                      className="flex-1 sm:flex-none px-6 bg-white border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50"
                    >
                      Done (keep in list)
                    </button>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusCell({ entry }) {
  if (entry.status === "created")
    return <span className="text-green-600 font-semibold">✓ Created</span>;
  if (entry.status === "creating")
    return <span className="text-gray-500">Creating…</span>;
  if (entry.status === "error")
    return (
      <span className="text-red-600 font-medium" title={entry.serverError}>
        ✕ {entry.serverError || "Error"}
      </span>
    );
  if (entry.errors.length)
    return (
      <span className="text-amber-600 font-medium" title={entry.errors.join("\n")}>
        {entry.errors.length} issue{entry.errors.length > 1 ? "s" : ""} — needs fixing
      </span>
    );
  return <span className="text-gray-500">Ready</span>;
}
